import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQuestionCircle, faChevronDown, faChevronUp, faSpinner,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import './HelpCenter.css';

const HelpCenter = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadFAQs();
  }, []);

  // FAQ content is maintained here rather than fetched: there is no FAQ
  // endpoint or admin screen to manage this content behind one, so the
  // previous call to supportAPI.getFAQ() always 404'd and fell through to
  // exactly this list — one guaranteed-failing request on every page load.
  const loadFAQs = () => {
    setFaqs([
        {
          id: '1',
          question: 'How do I reset my password?',
          answer: 'Click on "Forgot Password" on the login page. Enter your email address, and we\'ll send you instructions to reset your password.'
        },
        {
          id: '2',
          question: 'How do I contact support?',
          answer: 'You can create a support ticket from your dashboard, use the live chat feature, or email us at support@AutoboyExpress.com.'
        },
        {
          id: '3',
          question: 'How do I track my order?',
          answer: 'Go to your Orders section in the dashboard and click the "Track Order" button next to your order. You\'ll see real-time tracking updates.'
        },
        {
          id: '4',
          question: 'What payment methods do you accept?',
          answer: 'We accept credit cards, debit cards, bank transfers, and mobile money payments. All payments are secured with industry-standard encryption.'
        },
        {
          id: '5',
          question: 'How does the escrow system work?',
          answer: 'When you select escrow, your payment is held securely until you confirm receipt of your item. This protects both buyers and sellers.'
        },
        {
          id: '6',
          question: 'How do I become a seller?',
          answer: 'Click on "Become a Seller" in the menu, complete your profile, verify your identity through KYC, and start listing your products.'
        },
        {
          id: '7',
          question: 'What is the return policy?',
          answer: 'Returns are accepted within 7 days of delivery for items that are damaged, defective, or not as described. Contact the seller to initiate a return.'
        },
        {
          id: '8',
          question: 'How do I file a dispute?',
          answer: 'If there\'s an issue with your order, go to your Orders section, select the problematic order, and click "File Dispute". Provide details and evidence.'
        }
    ]);
    setLoading(false);
  };

  const toggleFAQ = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div className="AutoboyExpress-help-center">
        <div className="AutoboyExpress-help-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" color="#22C55E" />
          <p>Loading help center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="AutoboyExpress-help-center">
      {/* Header */}
      <div className="AutoboyExpress-help-header">
        <div className="AutoboyExpress-help-header-icon">
          <FontAwesomeIcon icon={faQuestionCircle} size="4x" />
        </div>
        <h1>How can we help you?</h1>
        <p>Find answers to common questions or contact our support team</p>
      </div>

      {/* FAQs */}
      <div className="AutoboyExpress-help-faqs">
        <h2>Frequently Asked Questions</h2>
        <div className="AutoboyExpress-help-faq-list">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className={`AutoboyExpress-help-faq-item ${expandedId === faq.id ? 'expanded' : ''}`}
            >
              <div
                className="AutoboyExpress-help-faq-question"
                onClick={() => toggleFAQ(faq.id)}
              >
                <h3>{faq.question}</h3>
                <FontAwesomeIcon
                  icon={expandedId === faq.id ? faChevronUp : faChevronDown}
                />
              </div>
              {expandedId === faq.id && (
                <div className="AutoboyExpress-help-faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Still Need Help */}
      <div className="AutoboyExpress-help-footer">
        <h2>Still need help?</h2>
        <p>Our support team is here to assist you</p>
        <button
          className="AutoboyExpress-help-contact-btn"
          onClick={() => navigate('/support')}
        >
          <FontAwesomeIcon icon={faEnvelope} />
          Contact Support
        </button>
      </div>
    </div>
  );
};

export default HelpCenter;
